import {
  ALL_FACES,
  CARVINGS,
  STAMPS,
  SUPERSTITIONS,
  faceCode,
  makeConfig,
  squareName,
  type MatchState,
  type OmenKind,
  type StampId,
} from '../../engine';
import { bestChainScore } from '../../analysis/search';
import { buildPosition } from '../../tutorial/position';
import { CampaignError, clone } from '../campaign';
import type { Profile } from '../types';
import {
  BARGAINS,
  BENCH_CURIOS,
  DETOURS,
  PLACEABLE_OMENS,
  SUPERSTITION_INFO,
  type BargainId,
  type DetourId,
} from './catalog';
import { isItemUnlocked } from './meta';
import { applyBargain, gain, losePawnToBroker, openCounter, type GainTarget } from './shop';
import type { NightRun, Offer, PuzzleSpec } from './types';
import { ledgerOf, mantelSlots, mantelUsed, nightRunOf, pay, rngFor } from './util';

/** Spoils and Detours after a High Table (docs/SHOP_DESIGN.md §13). */

/** Offers the Spoils of a High Table: a stamped card, a Curio (or Appraisal), an Appraisal. */
export function enterSpoils(p: Profile): void {
  const l = ledgerOf(p);
  const run = nightRunOf(p);
  const rng = rngFor(run, `spoils:${run.night}`);
  const k = rng.nextBelow(12);
  const card: Offer = {
    kind: 'card',
    card: { kind: 'normal', face: ALL_FACES[k]!, stamp: STAMPS[rng.nextBelow(STAMPS.length)]! },
  };
  const curios = [
    ...['chalk', 'pliers', 'bell-jar', 'pocket-watch', 'black-candle', 'rag-doll', 'spyglass'],
    ...BENCH_CURIOS,
  ];
  const second: Offer = l.unlocked.includes('curios')
    ? { kind: 'curio', id: curios[rng.nextBelow(curios.length)]! }
    : {
        kind: 'appraisal',
        id: (['ladder', 'echo', 'streak', 'capture'] as const)[rng.nextBelow(4)]!,
      };
  const third: Offer = {
    kind: 'appraisal',
    id: (['crown', 'landing', 'fullCircle', 'none'] as const)[rng.nextBelow(4)]!,
  };
  run.spoils = [card, second, third];
  run.phase = 'spoils';
}

/** Takes one of the Spoils (or none), then on to a Detour or the Counter. */
export function chooseSpoils(
  profile: Profile,
  index: number | null,
  target: GainTarget = {},
): Profile {
  const p = clone(profile);
  const run = nightRunOf(p);
  if (run.phase !== 'spoils' || !run.spoils) throw new CampaignError('no Spoils are waiting');
  if (index !== null) {
    const offer = run.spoils[index];
    if (!offer) throw new CampaignError('nothing there');
    gain(p, run, offer, target);
  }
  run.spoils = null;
  enterDetour(p);
  return p;
}

function enterDetour(p: Profile): void {
  const l = ledgerOf(p);
  const run = nightRunOf(p);
  if (!l.unlocked.includes('detours')) {
    run.table += 1;
    openCounter(p);
    return;
  }
  const rng = rngFor(run, `detours:${run.night}`);
  const pool = rng.shuffle([...DETOURS]);
  run.detour = { choices: pool.slice(0, 2), chosen: null, offers: [], done: false };
  run.phase = 'detour';
}

function rareOffer(
  p: Profile,
  run: NightRun,
  key: string,
  legendaryChance = 0,
  /** Already offered: a draw that lands on one of these draws again among the rest. */
  exclude?: ReadonlySet<string>,
): Offer | null {
  const l = ledgerOf(p);
  const rng = rngFor(run, key);
  const owned = new Set(run.mantel.map((i) => i.id));
  const rarity = legendaryChance && rng.nextBelow(legendaryChance) === 0 ? 'legendary' : 'rare';
  const pool = SUPERSTITIONS.filter(
    (id) =>
      SUPERSTITION_INFO[id].rarity === rarity &&
      !owned.has(id) &&
      (rarity === 'legendary' || isItemUnlocked(l, id)),
  );
  if (pool.length === 0) return null;
  const id = pool[rng.nextBelow(pool.length)]!;
  if (!exclude?.has(id)) return { kind: 'superstition', id };
  const rest = pool.filter((x) => !exclude.has(x));
  return rest.length > 0 ? { kind: 'superstition', id: rest[rng.nextBelow(rest.length)]! } : null;
}

/** Chooses one of the two Detours and lays out what it offers. */
export function chooseDetour(profile: Profile, id: DetourId): Profile {
  const p = clone(profile);
  const run = nightRunOf(p);
  const d = run.detour;
  if (run.phase !== 'detour' || !d || d.chosen) throw new CampaignError('no Detour is waiting');
  if (!d.choices.includes(id)) throw new CampaignError('that Detour is not on offer');
  d.chosen = id;
  const rng = rngFor(run, `detour:${run.night}:${id}`);
  switch (id) {
    case 'workbench': {
      const pool = rng.shuffle([...CARVINGS]).slice(0, 3);
      d.offers = pool.map((c) => ({ kind: 'carving', id: c }));
      break;
    }
    case 'seance': {
      const pool = rng.shuffle([...BARGAINS]).slice(0, 3);
      d.offers = pool.map((b) => ({ kind: 'bargain', id: b }));
      break;
    }
    case 'lost-and-found': {
      const all = [
        'chalk',
        'hourglass',
        'pliers',
        'scissors',
        'bell-jar',
        'smelling-salts',
        'pocket-watch',
        'black-candle',
        'lodestone',
        'rag-doll',
        'planchette',
        'cracked-mirror',
        'spyglass',
        'matchbook',
        ...BENCH_CURIOS,
      ];
      d.offers = rng
        .shuffle([...all])
        .slice(0, 3)
        .map((c) => ({ kind: 'curio', id: c }));
      break;
    }
    case 'fence': {
      // Three different things: never the same Rare twice.
      const offered = new Set<string>();
      d.offers = [0, 1, 2].flatMap((i) => {
        const o = rareOffer(p, run, `fence:${run.night}:${i}`, 0, offered);
        if (o?.kind !== 'superstition') return [];
        offered.add(o.id);
        return [o];
      });
      break;
    }
    case 'auction': {
      const o = rareOffer(p, run, `auction:${run.night}`, 8);
      d.offers = o ? [o] : [];
      break;
    }
    case 'cartographer':
      d.offers = rng
        .shuffle([...PLACEABLE_OMENS, 'thorns', 'draft', 'looking-glass', 'brazier'] as OmenKind[])
        .slice(0, 6)
        .map((k) => ({ kind: 'omen', id: k }));
      break;
    case 'puzzle-box':
      d.puzzle = makePuzzle(run, `puzzle:${run.night}`);
      break;
    default:
      break;
  }
  return p;
}

const detourOf = (run: NightRun, id: DetourId) => {
  const d = run.detour;
  if (run.phase !== 'detour' || !d || d.chosen !== id || d.done)
    throw new CampaignError('that Detour is not open');
  return d;
};

/** Workbench, Lost and Found, Séance: takes one of the Detour's offers. */
export function takeDetourOffer(
  profile: Profile,
  index: number,
  target: GainTarget = {},
  payWith: readonly string[] = [],
): Profile {
  const p = clone(profile);
  const run = nightRunOf(p);
  const d = run.detour;
  if (!d?.chosen) throw new CampaignError('no Detour is open');
  const offer = d.offers[index];
  if (!offer) throw new CampaignError('nothing there');
  detourOf(run, d.chosen);
  switch (d.chosen) {
    case 'workbench':
    case 'lost-and-found':
      gain(p, run, offer, target);
      if (d.chosen === 'lost-and-found') run.brass += 3;
      d.done = true;
      break;
    case 'seance': {
      if (offer.kind !== 'bargain') throw new CampaignError('not a Bargain');
      if (payWith.length !== 1) throw new CampaignError('the Séance takes one pawn');
      const id = payWith[0]!;
      const entry = run.roster.find((r) => r.id === id);
      if (!entry || entry.standIn) throw new CampaignError('pay with a named pawn');
      if (run.roster.length <= 6) throw new CampaignError('keep at least 6 pawns on the roster');
      losePawnToBroker(p, run, id);
      applyBargain(p, run, offer.id as BargainId, target);
      d.done = true;
      break;
    }
    case 'fence': {
      if (offer.kind !== 'superstition') throw new CampaignError('not for sale');
      const price = Math.max(
        1,
        Math.floor((SUPERSTITION_INFO[offer.id as keyof typeof SUPERSTITION_INFO]?.price ?? 8) / 2),
      );
      pay(run, price);
      gain(p, run, offer, target);
      d.offers.splice(index, 1);
      run.stolenGoods = true;
      break;
    }
    default:
      throw new CampaignError('this Detour works differently');
  }
  return p;
}

/** The Furnace: melt up to three cards free, or give one card a chosen Stamp. */
export function stokeFurnace(
  profile: Profile,
  melt: readonly number[],
  stamp?: { readonly card: number; readonly stamp: StampId },
): Profile {
  const p = clone(profile);
  const run = nightRunOf(p);
  const d = detourOf(run, 'furnace');
  if (stamp) {
    const c = run.deck[stamp.card];
    if (!c || !STAMPS.includes(stamp.stamp)) throw new CampaignError('choose a card and a Stamp');
    run.deck[stamp.card] = { ...c, stamp: stamp.stamp };
  } else {
    const set = [...new Set(melt)];
    if (set.length < 1 || set.length > 3 || set.some((i) => !run.deck[i]))
      throw new CampaignError('choose one to three cards');
    if (run.deck.length - set.length < 8)
      throw new CampaignError('the deck cannot go below 8 cards');
    run.deck = run.deck.filter((_, i) => !set.includes(i));
  }
  d.done = true;
  return p;
}

/** The Twins: two cards of one face become one, with a Double Stamp and a Foil Finish. */
export function fuseTwins(profile: Profile, a: number, b: number): Profile {
  const p = clone(profile);
  const run = nightRunOf(p);
  const d = detourOf(run, 'twins');
  const x = run.deck[a];
  const y = run.deck[b];
  if (
    a === b ||
    !x ||
    !y ||
    x.kind !== 'normal' ||
    y.kind !== 'normal' ||
    faceCode(x.face) !== faceCode(y.face)
  )
    throw new CampaignError('choose two cards with the same face');
  run.deck[a] = { kind: 'normal', face: x.face, stamp: 'double-stamp', finish: 'foil' };
  run.deck.splice(b, 1);
  d.done = true;
  return p;
}

/** The Auction: a sealed bid against the three regulars (docs/SHOP_DESIGN.md §13.1). */
export function bidAtAuction(profile: Profile, bid: number): Profile {
  const p = clone(profile);
  const run = nightRunOf(p);
  const d = detourOf(run, 'auction');
  const offer = d.offers[0];
  if (!offer) throw new CampaignError('nothing is up for auction');
  if (!Number.isInteger(bid) || bid < 1) throw new CampaignError('bid a whole amount of brass');
  const rng = rngFor(run, `auction-bids:${run.night}`);
  const rivals = [0, 1, 2].map(() => 3 + rng.nextBelow(6 + 3 * run.night));
  const top = Math.max(...rivals);
  if (bid > top) {
    pay(run, bid);
    gain(p, run, offer);
    d.offers = [];
  }
  d.done = true;
  d.rivals = rivals;
  return p;
}

/** The Confessional: every IOU is forgiven; ¤8 lifts the rot from Rotting items. */
export function confess(profile: Profile, liftRot: boolean): Profile {
  const p = clone(profile);
  const run = nightRunOf(p);
  const d = detourOf(run, 'confessional');
  run.deck = run.deck.filter((c) => !c.iou);
  if (liftRot) {
    pay(run, 8);
    delete run.counters.rotting;
    for (const k of Object.keys(run.counters))
      if (k.startsWith('rot:') || k.startsWith('rotting:')) delete run.counters[k];
  }
  d.done = true;
  return p;
}

/** The Cartographer: three Omens chosen for every table of the next Night. */
export function chartOmens(profile: Profile, picks: readonly number[]): Profile {
  const p = clone(profile);
  const run = nightRunOf(p);
  const d = detourOf(run, 'cartographer');
  const set = [...new Set(picks)];
  if (set.length !== 3 || set.some((i) => d.offers[i]?.kind !== 'omen'))
    throw new CampaignError('choose three Omens');
  run.chartedOmens = set.map((i) => (d.offers[i] as { id: OmenKind }).id);
  d.done = true;
  return p;
}

// ---------------------------------------------------------------------------------------------
// The Puzzle Box
// ---------------------------------------------------------------------------------------------

/** The match config of a puzzle: South brings its kit (without Curios); North has nothing. */
export function puzzleConfig(run: NightRun) {
  const kit = {
    mantel: run.mantel,
    curios: [],
    appraisals: { ...run.appraisals },
    discovered: [...run.discovered],
    brass: Math.max(0, run.brass),
    counters: { chains: run.counters.chains ?? 0, pawned: run.counters.pawned ?? 0 },
    bonuses: {},
    slots: mantelSlots(run),
  };
  const empty = {
    mantel: [],
    curios: [],
    appraisals: {},
    discovered: [],
    brass: 0,
    counters: {},
    bonuses: {},
  };
  return makeConfig({
    mode: 'campaign',
    preset: 'A',
    seating: { players: ['player', 'puzzle'], assignment: 'fixed' },
    ledger: { kits: { S: kit, N: empty }, omens: [], houseRules: [], anteBlind: true },
  });
}

/** Builds the position of a puzzle. */
export function puzzlePosition(run: NightRun, spec: PuzzleSpec): MatchState {
  return buildPosition({
    matchConfig: puzzleConfig(run),
    south: [...spec.south],
    north: [...spec.north],
    hands: { S: [...spec.hand], N: [] },
    top: spec.top,
    toAct: 'S',
    phase: 'ACTION',
    seed: spec.seed,
  });
}

/** A solvable puzzle: a random position whose target is three quarters of its best chain. */
export function makePuzzle(run: NightRun, key: string): PuzzleSpec {
  for (let attempt = 0; attempt < 30; attempt++) {
    const rng = rngFor(run, `${key}:${attempt}`);
    const squares = new Set<string>();
    const pick = (ys: readonly number[]): string => {
      for (;;) {
        const sq = { x: rng.nextBelow(6), y: ys[rng.nextBelow(ys.length)]! };
        const name = squareName(sq);
        if (!squares.has(name)) {
          squares.add(name);
          return name;
        }
      }
    };
    const south = [pick([1, 2]), pick([1, 2, 3])];
    const north = [pick([3, 4]), pick([3, 4]), pick([2, 3, 4])];
    const codes = ALL_FACES.map(faceCode);
    const hand = [0, 1, 2, 3].map(() => codes[rng.nextBelow(codes.length)]!);
    const counts = new Map<string, number>();
    if (hand.some((c) => (counts.set(c, (counts.get(c) ?? 0) + 1).get(c) ?? 0) > 2)) continue;
    const spec: PuzzleSpec = {
      seed: `${run.seed}:${key}:${attempt}`,
      south,
      north,
      hand,
      top: null,
      target: 0,
    };
    let state: MatchState;
    try {
      state = puzzlePosition(run, spec);
    } catch {
      continue;
    }
    const best = bestChainScore(state, 'S', 6000);
    if (best.score < 10) continue;
    return { ...spec, target: Math.max(10, Math.floor(best.score * 0.75)) };
  }
  return {
    seed: `${run.seed}:${key}`,
    south: ['C2'],
    north: ['C4'],
    hand: ['R1', 'R1'],
    top: null,
    target: 1,
  };
}

/** The puzzle's outcome: solved, it pays a Rare Superstition (or ¤10 when the Mantel is full). */
export function solvePuzzle(profile: Profile, score: number): Profile {
  const p = clone(profile);
  const run = nightRunOf(p);
  const d = detourOf(run, 'puzzle-box');
  const target = d.puzzle?.target ?? Infinity;
  if (score >= target) {
    const offer = rareOffer(p, run, `puzzle-prize:${run.night}`);
    if (offer && mantelUsed(run) < mantelSlots(run)) gain(p, run, offer);
    else run.brass += 10;
    d.solved = true;
  }
  d.done = true;
  return p;
}

/** Leaves the Detour for the Counter. */
export function finishDetour(profile: Profile): Profile {
  const p = clone(profile);
  const run = nightRunOf(p);
  if (run.phase !== 'detour') throw new CampaignError('no Detour is open');
  run.detour = null;
  run.table += 1;
  openCounter(p);
  return p;
}
